import type { RNG } from "../core/RNG";
import { Hero, type LevelUp } from "./Hero";
import { labelDuplicates, Monster } from "./Monster";
import type { Party } from "./Party";
import type { ItemDef, Registry, SkillDef, TargetKind } from "./Skills";
import { Combatant } from "./Stats";

// ------------------------------------------------------------------ commands

export type Command =
  | { kind: "attack"; target: Combatant }
  | { kind: "skill"; skill: string; target?: Combatant }
  | { kind: "item"; item: string; target?: Combatant }
  | { kind: "defend" }
  | { kind: "flee" };

// -------------------------------------------------------------------- events

export type BattleOutcome = "ongoing" | "win" | "lose" | "fled";

export type BattleEvent =
  | { type: "message"; text: string }
  | { type: "attack"; actor: Combatant; target: Combatant; damage: number; critical: boolean; missed: boolean; text: string }
  | { type: "skill"; actor: Combatant; skill: SkillDef; text: string }
  | { type: "item"; actor: Combatant; item: ItemDef; text: string }
  | { type: "damage"; target: Combatant; amount: number; text?: string }
  | { type: "heal"; target: Combatant; amount: number; text: string }
  | { type: "status"; target: Combatant; text: string }
  | { type: "defend"; actor: Combatant; text: string }
  | { type: "defeated"; target: Combatant; text: string }
  | { type: "flee"; actor: Combatant; success: boolean; text: string }
  | { type: "levelup"; hero: Hero; levelUp: LevelUp; text: string }
  | { type: "result"; outcome: BattleOutcome; exp: number; gold: number; text?: string };

export interface BattleDeps {
  rng: RNG;
  skills: Registry<SkillDef>;
  items: Registry<ItemDef>;
}

interface PlannedAction {
  actor: Combatant;
  command: Command;
  initiative: number;
}

/**
 * Headless turn engine. It knows nothing about rendering: a round of commands
 * goes in, an ordered list of events comes out, and the scene animates them.
 * That split makes the rules testable and lets any presentation layer sit on
 * top of them.
 */
export class Battle {
  readonly heroes: Hero[];
  readonly monsters: Monster[];
  outcome: BattleOutcome = "ongoing";
  round = 0;

  private earnedExp = 0;
  private earnedGold = 0;

  constructor(
    private party: Party,
    monsters: Monster[],
    private deps: BattleDeps,
  ) {
    this.heroes = party.members;
    this.monsters = monsters;
    labelDuplicates(this.monsters);
  }

  get activeMonsters(): Monster[] {
    return this.monsters.filter((m) => m.active);
  }

  get livingHeroes(): Hero[] {
    return this.heroes.filter((h) => h.alive);
  }

  /** Opening line: "A slime draws near!" or the plural form. */
  encounterText(): string {
    const counts = new Map<string, number>();
    for (const m of this.monsters) counts.set(m.def.name, (counts.get(m.def.name) ?? 0) + 1);
    const parts = [...counts].map(([name, n]) => (n > 1 ? `${n} ${name}s` : `A ${name}`));
    const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
    return `${list} draws near!`;
  }

  // ------------------------------------------------------------- resolution

  /** Runs one full round and returns everything that happened, in order. */
  resolveRound(commands: Map<Hero, Command>): BattleEvent[] {
    const events: BattleEvent[] = [];
    this.round++;

    // Guarding takes effect for the whole round, including against faster foes.
    for (const [hero, command] of commands) {
      if (command.kind === "defend") hero.defending = true;
    }

    const actions = this.planActions(commands);

    for (const action of actions) {
      if (this.outcome !== "ongoing") break;
      if (!this.isActive(action.actor)) continue;
      if (this.handleSleep(action.actor, events)) continue;

      this.execute(action.actor, action.command, events);
      this.checkOutcome(events);
    }

    // End of round housekeeping.
    for (const c of [...this.heroes, ...this.monsters]) {
      c.defending = false;
      for (const [status, turns] of c.statuses) {
        if (turns <= 1) c.statuses.delete(status);
        else c.statuses.set(status, turns - 1);
      }
    }

    if (this.outcome === "win") this.awardSpoils(events);
    return events;
  }

  private planActions(commands: Map<Hero, Command>): PlannedAction[] {
    const rng = this.deps.rng;
    const actions: PlannedAction[] = [];

    for (const hero of this.livingHeroes) {
      const command = commands.get(hero);
      if (!command) continue;
      actions.push({
        actor: hero,
        command,
        // Fleeing is resolved first; it is the whole point of running away.
        initiative: command.kind === "flee" ? 9999 : hero.stats.agility * rng.float(0.75, 1.25),
      });
    }

    for (const monster of this.activeMonsters) {
      actions.push({
        actor: monster,
        command: this.chooseMonsterCommand(monster),
        initiative: monster.stats.agility * rng.float(0.75, 1.25),
      });
    }

    return actions.sort((a, b) => b.initiative - a.initiative);
  }

  private chooseMonsterCommand(monster: Monster): Command {
    const rng = this.deps.rng;

    for (const use of monster.def.skills ?? []) {
      if (use.whenHpBelow !== undefined && monster.hpRatio > use.whenHpBelow) continue;
      const skill = this.deps.skills.find(use.skill);
      if (!skill || monster.mp < skill.mp) continue;
      if (!rng.chance(use.chance)) continue;
      const target = this.pickTarget(skill.target, monster);
      if (!target && skill.target !== "none" && skill.target !== "all-enemies" && skill.target !== "all-allies") {
        continue;
      }
      return { kind: "skill", skill: skill.id, target: target ?? undefined };
    }

    if (monster.def.cowardice && monster.hpRatio < 0.25 && rng.chance(monster.def.cowardice)) {
      return { kind: "flee" };
    }

    const living: Combatant[] = this.livingHeroes;
    return { kind: "attack", target: rng.pick(living.length ? living : this.heroes) };
  }

  private execute(actor: Combatant, command: Command, events: BattleEvent[]): void {
    switch (command.kind) {
      case "attack":
        this.doAttack(actor, this.retarget(command.target, actor), events);
        break;
      case "defend":
        actor.defending = true;
        events.push({ type: "defend", actor, text: `${actor.name} braces for the next blow.` });
        break;
      case "flee":
        this.doFlee(actor, events);
        break;
      case "skill": {
        const skill = this.deps.skills.get(command.skill);
        if (!actor.spendMp(skill.mp)) {
          events.push({ type: "message", text: `${actor.name} does not have enough MP!` });
          return;
        }
        const cast = skill.castMessage?.(actor.name) ?? `${actor.name} casts ${skill.name}!`;
        events.push({ type: "skill", actor, skill, text: cast });
        this.applySkill(actor, skill, command.target, events);
        break;
      }
      case "item": {
        const item = this.deps.items.get(command.item);
        if (!this.party.removeItem(item.id)) {
          events.push({ type: "message", text: `There are no ${item.name}s left!` });
          return;
        }
        events.push({ type: "item", actor, item, text: `${actor.name} uses the ${item.name}.` });
        if (item.use) this.applySkill(actor, this.deps.skills.get(item.use), command.target, events);
        break;
      }
    }
  }

  /** Redirects an action if its target died before the actor's turn came up. */
  private retarget(target: Combatant, actor: Combatant): Combatant {
    if (this.isActive(target)) return target;
    const pool: Combatant[] = actor instanceof Hero ? this.activeMonsters : this.livingHeroes;
    return pool.length > 0 ? this.deps.rng.pick(pool) : target;
  }

  private doAttack(actor: Combatant, target: Combatant, events: BattleEvent[]): void {
    const rng = this.deps.rng;
    const attack = actor.stats.attack;
    const defense = target.defending ? target.stats.defense * 2 : target.stats.defense;

    if (rng.chance(target.stats.evasion ?? 0)) {
      events.push({
        type: "attack",
        actor,
        target,
        damage: 0,
        critical: false,
        missed: true,
        text: `${actor.name} attacks! ${target.name} dodges the blow!`,
      });
      return;
    }

    // 1-in-32 excellent move, ignoring defense entirely — the DQ critical.
    const critical = rng.chance(1 / 32);
    let damage: number;
    if (critical) {
      damage = Math.max(1, Math.floor((attack / 2) * rng.float(1.0, 1.3)));
    } else {
      const base = attack / 2 - defense / 4;
      damage =
        base > 0
          ? Math.max(1, Math.floor(base * rng.float(0.85, 1.15)))
          : // Chip damage: even a hopeless attack sometimes scratches.
            rng.chance(0.5)
            ? 1
            : 0;
    }

    const dealt = target.damage(damage);
    const text = critical
      ? `${actor.name} attacks! A terrific blow! ${target.name} takes ${dealt} damage!`
      : dealt === 0
        ? `${actor.name} attacks! ${target.name} is unharmed.`
        : `${actor.name} attacks! ${target.name} takes ${dealt} damage!`;

    events.push({ type: "attack", actor, target, damage: dealt, critical, missed: false, text });
    this.checkDefeat(target, events);
  }

  private doFlee(actor: Combatant, events: BattleEvent[]): void {
    const rng = this.deps.rng;
    const enemies = actor instanceof Hero ? this.activeMonsters : this.livingHeroes;
    const enemyAgility = enemies.reduce((sum, e) => sum + e.stats.agility, 0) / Math.max(1, enemies.length);
    const chance = Math.min(0.95, Math.max(0.15, (actor.stats.agility * rng.float(0.5, 1.5)) / (enemyAgility * 1.4)));
    const success = rng.chance(chance);

    if (actor instanceof Hero) {
      events.push({
        type: "flee",
        actor,
        success,
        text: success ? `${actor.name} flees from the battle!` : `${actor.name} tries to flee — but is blocked!`,
      });
      if (success) this.outcome = "fled";
      return;
    }

    const monster = actor as Monster;
    if (success) monster.fled = true;
    events.push({
      type: "flee",
      actor,
      success,
      text: success ? `${monster.name} runs away!` : `${monster.name} tries to run — but cannot escape!`,
    });
  }

  private applySkill(
    actor: Combatant,
    skill: SkillDef,
    explicitTarget: Combatant | undefined,
    events: BattleEvent[],
  ): void {
    const targets = this.resolveTargets(actor, skill.target, explicitTarget);
    const rng = this.deps.rng;

    if (skill.kind === "flee") {
      this.outcome = actor instanceof Hero ? "fled" : this.outcome;
      events.push({ type: "message", text: `${actor.name} vanishes in a puff of smoke!` });
      return;
    }

    for (const target of targets) {
      switch (skill.kind) {
        case "damage": {
          const power = skill.power ?? 0;
          const variance = skill.variance ?? 0.2;
          let amount = Math.max(1, Math.round(power * rng.float(1 - variance, 1 + variance)));
          if (!skill.ignoresDefense) amount = Math.max(1, amount - Math.floor(target.stats.defense / 4));
          if (target.defending) amount = Math.max(1, Math.floor(amount / 2));
          const dealt = target.damage(amount);
          events.push({ type: "damage", target, amount: dealt, text: `${target.name} takes ${dealt} damage!` });
          this.checkDefeat(target, events);
          break;
        }
        case "heal": {
          const power = skill.power ?? 0;
          const variance = skill.variance ?? 0.15;
          const amount = target.heal(Math.round(power * rng.float(1 - variance, 1 + variance)));
          events.push({
            type: "heal",
            target,
            amount,
            text: amount > 0 ? `${target.name} recovers ${amount} HP.` : `${target.name} is already at full health.`,
          });
          break;
        }
        case "revive": {
          if (target.alive) {
            events.push({ type: "message", text: `${target.name} is not in need of that.` });
            break;
          }
          target.hp = Math.max(1, Math.round(target.maxHp * (skill.power ?? 0.5)));
          events.push({ type: "heal", target, amount: target.hp, text: `${target.name} returns to life!` });
          break;
        }
        case "status": {
          if (!skill.status) break;
          const applied = rng.chance(skill.statusChance ?? 0.5);
          if (applied) target.applyStatus(skill.status, skill.statusTurns ?? 3);
          events.push({
            type: "status",
            target,
            text: applied
              ? `${target.name} falls fast asleep!`
              : `${target.name} shrugs it off!`,
          });
            break;
        }
      }
    }
  }

  private resolveTargets(actor: Combatant, kind: TargetKind, explicit?: Combatant): Combatant[] {
    const allies: Combatant[] = actor instanceof Hero ? this.livingHeroes : this.activeMonsters;
    const enemies: Combatant[] = actor instanceof Hero ? this.activeMonsters : this.livingHeroes;

    switch (kind) {
      case "self":
        return [actor];
      case "enemy":
        return explicit && this.isActive(explicit) ? [explicit] : enemies.slice(0, 1);
      case "ally":
        // Revival targets the dead, so fall back to the full roster.
        return explicit ? [explicit] : allies.slice(0, 1);
      case "all-enemies":
        return enemies;
      case "all-allies":
        return allies;
      case "none":
        return [];
    }
  }

  private pickTarget(kind: TargetKind, actor: Combatant): Combatant | null {
    const rng = this.deps.rng;
    if (kind === "enemy") {
      const pool: Combatant[] = actor instanceof Hero ? this.activeMonsters : this.livingHeroes;
      return pool.length ? rng.pick(pool) : null;
    }
    if (kind === "ally") {
      const pool: Combatant[] = actor instanceof Hero ? this.livingHeroes : this.activeMonsters;
      // Heal whoever needs it most.
      const hurt = pool.filter((c) => c.hp < c.maxHp);
      if (hurt.length === 0) return null;
      return hurt.reduce((worst, c) => (c.hpRatio < worst.hpRatio ? c : worst));
    }
    if (kind === "self") return actor;
    return null;
  }

  private handleSleep(actor: Combatant, events: BattleEvent[]): boolean {
    if (!actor.has("sleep")) return false;
    if (this.deps.rng.chance(0.34)) {
      actor.clearStatus("sleep");
      events.push({ type: "message", text: `${actor.name} wakes up!` });
      return false;
    }
    events.push({ type: "message", text: `${actor.name} is fast asleep.` });
    return true;
  }

  private isActive(c: Combatant): boolean {
    if (c instanceof Monster) return c.active;
    return c.alive;
  }

  private checkDefeat(target: Combatant, events: BattleEvent[]): void {
    if (target.alive) return;
    if (target instanceof Monster) {
      this.earnedExp += target.def.exp;
      const gold = target.def.gold;
      this.earnedGold += Array.isArray(gold) ? this.deps.rng.int(gold[0], gold[1]) : gold;
      events.push({ type: "defeated", target, text: `${target.name} is defeated!` });
    } else {
      events.push({ type: "defeated", target, text: `${target.name} has fallen!` });
    }
  }

  private checkOutcome(events: BattleEvent[]): void {
    if (this.outcome !== "ongoing") return;
    if (this.activeMonsters.length === 0) {
      this.outcome = "win";
    } else if (this.livingHeroes.length === 0) {
      this.outcome = "lose";
      events.push({ type: "result", outcome: "lose", exp: 0, gold: 0 });
    }
  }

  private awardSpoils(events: BattleEvent[]): void {
    this.party.gold += this.earnedGold;
    events.push({
      type: "result",
      outcome: "win",
      exp: this.earnedExp,
      gold: this.earnedGold,
      text: `Victory! The party earns ${this.earnedExp} experience and ${this.earnedGold} gold.`,
    });

    for (const hero of this.livingHeroes) {
      for (const levelUp of hero.gainExp(this.earnedExp)) {
        const gains = Object.entries(levelUp.gains)
          .map(([key, value]) => `${STAT_LABELS[key] ?? key} +${value}`)
          .join("  ");
        events.push({
          type: "levelup",
          hero,
          levelUp,
          text: `${hero.name} reaches level ${levelUp.level}!\n${gains}`,
        });
        for (const skill of levelUp.learned) {
          events.push({
            type: "message",
            text: `${hero.name} learns ${this.deps.skills.get(skill).name}!`,
          });
        }
      }
    }
  }
}

const STAT_LABELS: Record<string, string> = {
  maxHp: "HP",
  maxMp: "MP",
  attack: "Attack",
  defense: "Defense",
  agility: "Agility",
};
